import React from 'react';
import styled from 'styled-components';
import { media } from '../theme';
import { Prediction } from '../types/predictions';

interface TimeFrameSelectorProps {
  selectedTimeFrame: string;
  onTimeFrameChange: (timeFrame: string) => void;
  isDarkMode: boolean;
  predictions?: Prediction[]; // Add predictions for average calculation
  currentAsset?: 'btc' | 'mstr'; // Add asset type for proper filtering
}

const TimeFrameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  
  ${media.mobile} {
    padding: 10px 14px;
    gap: 8px;
    border-radius: 6px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  ${media.mobile} {
    gap: 8px;
  }
`;

const AverageSection = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.text};
  font-size: 14px;
  font-weight: 600;
  margin-left: 16px;
  
  ${media.mobile} {
    font-size: 12px;
    margin-left: 12px;
  }
`;

const AverageDisplay = styled.div`
  color: ${props => props.theme.primary};
  font-weight: 700;
  margin-left: 8px;
`;

const TimeFrameLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.text};
  font-size: 14px;
  white-space: nowrap;
  
  ${media.mobile} {
    font-size: 13px;
  }
`;

const TimeFrameButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  
  ${media.mobile} {
    gap: 6px;
  }
`;

const TimeFrameButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border: 2px solid ${props => props.$active ? props.theme.primary : props.theme.border};
  background: ${props => props.$active ? props.theme.primary : props.theme.surface};
  color: ${props => props.$active ? 'white' : props.theme.text};
  border-radius: 16px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.$active ? props.theme.primary : props.theme.hoverBackground};
  }
  
  ${media.mobile} {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

const TimeFrameSelector: React.FC<TimeFrameSelectorProps> = ({
  selectedTimeFrame,
  onTimeFrameChange,
  isDarkMode,
  predictions = [],
  currentAsset = 'btc'
}) => {
  const timeFrames = [
    { value: '30D', label: '30 Days' },
    { value: '3M', label: '3 Months' },
    { value: '6M', label: '6 Months' },
    { value: '1Y', label: '1 Year' },
    { value: '2Y', label: '2 Years' },
    { value: '3Y', label: '3 Years' },
    { value: '5Y', label: '5 Years' },
    { value: 'ALL', label: 'All Time' }
  ];

  // Calculate average prediction for selected time frame
  const calculateAverageForTimeFrame = (timeFrame: string): number | null => {
    const now = new Date();
    let endDate: Date;
    
    // Calculate the end date based on time frame
    switch (timeFrame) {
      case '30D':
        endDate = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
        break;
      case '3M':
        endDate = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));
        break;
      case '6M':
        endDate = new Date(now.getTime() + (180 * 24 * 60 * 60 * 1000));
        break;
      case '1Y':
        endDate = new Date(now.getTime() + (365 * 24 * 60 * 60 * 1000));
        break;
      case '2Y':
        endDate = new Date(now.getTime() + (2 * 365 * 24 * 60 * 60 * 1000));
        break;
      case '3Y':
        endDate = new Date(now.getTime() + (3 * 365 * 24 * 60 * 60 * 1000));
        break;
      case '5Y':
        endDate = new Date(now.getTime() + (5 * 365 * 24 * 60 * 60 * 1000));
        break;
      case 'ALL':
        endDate = new Date(now.getTime() + (10 * 365 * 24 * 60 * 60 * 1000)); // 10 years for "all time"
        break;
      default:
        return null;
    }

    // Filter predictions that are:
    // 1. Pending status
    // 2. Have target dates within the time frame
    // 3. Match current asset type
    const relevantPredictions = predictions.filter(pred => {
      // Check if prediction is pending
      if (pred.success_status !== 'pending') return false;
      
      // Check asset type match - if no asset_type field, assume it's BTC
      // MSTR predictions should have asset_type === 'mstr'
      const predAssetType = pred.asset_type || 'btc';
      if (currentAsset === 'mstr' && predAssetType !== 'mstr') return false;
      if (currentAsset === 'btc' && predAssetType === 'mstr') return false;
      
      // Get target date from prediction
      let targetDate: Date | null = null;
      if (pred.target_time) {
        targetDate = new Date(pred.target_time);
      } else if (pred.end_date) {
        targetDate = new Date(pred.end_date);
      }
      
      // Check if target date is within time frame
      if (targetDate && targetDate >= now && targetDate <= endDate) {
        return true;
      }
      
      return false;
    });

    if (relevantPredictions.length === 0) return null;

    // Calculate average target price
    const validPrices: number[] = [];
    
    relevantPredictions.forEach(pred => {
      let targetPrice: number | null = null;
      
      // Helper function to safely parse price value
      const parsePrice = (value: any): number | null => {
        if (value === undefined || value === null) return null;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const cleaned = value.replace(/[$,]/g, '');
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        }
        return null;
      };
      
      // Extract target price based on prediction type
      if (pred.prediction_type === 'target') {
        targetPrice = parsePrice(pred.price_target) || 
                     parsePrice(pred.price_prediction) || 
                     parsePrice(pred.target_price);
      } else if (pred.prediction_type === 'pricetimerange' || pred.prediction_type === 'range') {
        targetPrice = parsePrice(pred.price_min);
      } else if (pred.prediction_type === 'top') {
        targetPrice = parsePrice(pred.price_max);
      } else if (pred.prediction_type === 'bottom') {
        targetPrice = parsePrice(pred.price_min);
      }
      
      if (targetPrice && targetPrice > 0) {
        validPrices.push(targetPrice);
      }
    });

    if (validPrices.length === 0) return null;
    
    // Calculate average
    const average = validPrices.reduce((sum, price) => sum + price, 0) / validPrices.length;
    return average;
  };

  const averagePrice = calculateAverageForTimeFrame(selectedTimeFrame);
  
  const formatPrice = (price: number): string => {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(1)}M`;
    } else if (price >= 1000) {
      return `$${(price / 1000).toFixed(0)}k`;
    } else {
      return `$${price.toFixed(0)}`;
    }
  };

  const getTimeFrameLabel = (timeFrame: string): string => {
    switch (timeFrame) {
      case '1Y': return '1 year';
      case '2Y': return '2 years';
      case '3Y': return '3 years';
      case '5Y': return '5 years';
      case 'ALL': return 'all time';
      default: return timeFrame.toLowerCase();
    }
  };

  return (
    <TimeFrameContainer>
      <LeftSection>
        <TimeFrameLabel>Time Frame:</TimeFrameLabel>
        <TimeFrameButtonGroup>
          {timeFrames.map(({ value, label }) => (
            <TimeFrameButton
              key={value}
              $active={selectedTimeFrame === value}
              onClick={() => onTimeFrameChange(value)}
              title={`Show predictions and price for ${label.toLowerCase()}`}
            >
              {label}
            </TimeFrameButton>
          ))}
        </TimeFrameButtonGroup>
      </LeftSection>
      
      {averagePrice && (
        <AverageSection>
          Average target {getTimeFrameLabel(selectedTimeFrame)}:
          <AverageDisplay>
            {formatPrice(averagePrice)}
          </AverageDisplay>
        </AverageSection>
      )}
    </TimeFrameContainer>
  );
};

export default TimeFrameSelector;
